import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Stack, styled, Text } from 'shell/components';

const FeedbackTypeCard = ({ type, isSelected, onClick, disabled = false, appearance = 'default', ...rest }) => {
    const { t } = useTranslation('assignment');
    const id = useId();
    const isQuizEdit = appearance === 'quiz-edit';
    const getCardContent = (type) => {
        if (type === 'rubric') {
            return (
                <Stack spacing={3}>
                    <svg
                        width='222'
                        height='81'
                        viewBox='0 0 222 81'
                        fill='none'
                        role='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <rect x='63.5801' y='13.9844' width='94.84' height='24.3179' rx='1.77271' fill='#D1FFE4' />
                        <path
                            d='M76.2761 22.9907C76.3256 23.0446 76.3649 23.1086 76.3917 23.179C76.4185 23.2495 76.4323 23.325 76.4323 23.4013C76.4323 23.4776 76.4185 23.5531 76.3917 23.6236C76.3649 23.6941 76.3256 23.7581 76.2761 23.8119L71.4907 29.0317C71.4413 29.0857 71.3827 29.1285 71.3181 29.1578C71.2535 29.187 71.1842 29.2021 71.1143 29.2021C71.0443 29.2021 70.9751 29.187 70.9105 29.1578C70.8459 29.1285 70.7872 29.0857 70.7378 29.0317L68.611 26.7118C68.5112 26.6029 68.4551 26.4552 68.4551 26.3012C68.4551 26.1472 68.5112 25.9995 68.611 25.8906C68.7108 25.7817 68.8463 25.7205 68.9875 25.7205C69.1286 25.7205 69.2641 25.7817 69.3639 25.8906L71.1143 27.801L75.5232 22.9907C75.5726 22.9367 75.6312 22.8938 75.6958 22.8646C75.7604 22.8354 75.8297 22.8203 75.8996 22.8203C75.9696 22.8203 76.0388 22.8354 76.1034 22.8646C76.168 22.8938 76.2267 22.9367 76.2761 22.9907Z'
                            fill='#007832'
                        />
                        <rect x='63.5801' y='41.8477' width='94.84' height='24.3179' rx='1.77271' fill='#D3CCFF' />
                        <path
                            d='M76.2141 53.7808C76.6413 53.346 76.4057 52.5882 75.8148 52.4965L74.3093 52.2691C74.0738 52.2332 73.8701 52.0776 73.7663 51.8583L73.0914 50.4383C72.9597 50.1591 72.7001 50.0195 72.4446 50.0195C72.185 50.0195 71.9295 50.1591 71.7977 50.4383L71.1228 51.8583C71.019 52.0816 70.8154 52.2332 70.5798 52.2691L69.0744 52.4965C68.4834 52.5842 68.2438 53.346 68.6751 53.7808L69.7652 54.8856C69.9369 55.0571 70.0128 55.3084 69.9728 55.5517L69.7173 57.1152C69.6374 57.6019 70.0088 57.9967 70.428 57.9967C70.5398 57.9967 70.6556 57.9688 70.7635 57.909L72.1131 57.1711C72.217 57.1152 72.3328 57.0833 72.4486 57.0833C72.5644 57.0833 72.6802 57.1113 72.784 57.1711L74.1336 57.909C74.2455 57.9688 74.3573 57.9967 74.4691 57.9967C74.8884 57.9967 75.2597 57.6019 75.1798 57.1152L74.9243 55.5517C74.8844 55.3084 74.9602 55.0571 75.1319 54.8856L76.2141 53.7808ZM74.4531 54.1677C74.0538 54.5705 73.8741 55.1529 73.9659 55.7232L74.1416 56.7842L73.2272 56.2856C72.9876 56.154 72.7161 56.0822 72.4406 56.0822C72.165 56.0822 71.8975 56.15 71.6539 56.2856L70.7395 56.7842L70.9152 55.7232C71.011 55.1529 70.8273 54.5705 70.428 54.1677L69.6893 53.4178L70.7115 53.2623C71.2626 53.1785 71.7378 52.8195 71.9814 52.301L72.4366 51.3358L72.8918 52.301C73.1394 52.8195 73.6145 53.1785 74.1616 53.2623L75.1838 53.4178L74.4531 54.1677Z'
                            fill='#1E2481'
                        />
                    </svg>
                    <Stack alignItems={'center'} spacing={2}>
                        <Text color={'primary'} variant={'p3'}>
                            {t('feedback-section.settings.ai-generated-feedback', 'AI Generated Feedback')}
                        </Text>
                        <Text align={'center'} variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.rubric-based-feedback', 'Rubric-Based Feedback')}
                        </Text>
                        <Text align={'center'}>
                            {t(
                                'feedback-section.settings.rubric-based-feedback-description',
                                'Feedback will include one piece of positive feedback and one suggestion for improvement related to each criterion.'
                            )}
                        </Text>
                    </Stack>
                </Stack>
            );
        }
        if (type === 'system') {
            return (
                <Stack spacing={3}>
                    <svg
                        width='222'
                        height='81'
                        viewBox='0 0 222 81'
                        fill='none'
                        role='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <rect x='63.5796' y='1.07477' width='94.8409' height='16.8409' rx='1.77273' fill='#D3CCFF' />
                        <g clipPath={`url(#${id}-clip)`}>
                            <mask id={`${id}-mask`} fill='white'>
                                <path d='M99.9207 17.9158V71.0976H79.5343C78.5552 71.0976 77.7616 70.304 77.7616 69.3249V17.9158H99.9207Z' />
                            </mask>
                            <line
                                x1='78.6479'
                                y1='49.3816'
                                x2='99.9207'
                                y2='49.3816'
                                stroke='#BFBEC6'
                                strokeWidth='0.886364'
                            />
                            <line
                                x1='78.6479'
                                y1='29.8817'
                                x2='99.9207'
                                y2='29.8817'
                                stroke='#BFBEC6'
                                strokeWidth='0.886364'
                            />
                        </g>
                        <path
                            d='M99.9207 17.9158V71.0976V17.9158ZM79.5343 71.984C78.0657 71.984 76.8752 70.7935 76.8752 69.3249V17.9158H78.6479V69.3249C78.6479 69.8144 79.0448 70.2113 79.5343 70.2113V71.984ZM77.7616 17.9158H99.9207H77.7616ZM99.9207 71.984H79.5343C78.0657 71.984 76.8752 70.7935 76.8752 69.3249H78.6479C78.6479 69.8144 79.0448 70.2113 79.5343 70.2113H99.9207V71.984Z'
                            fill='#BFBEC6'
                            mask={`url(#${id}-mask)`}
                        />
                        <rect x='99.9207' y='21.4611' width='58.5' height='16.8409' rx='1.77273' fill='#D3CCFF' />
                        <path
                            d='M112.555 29.6542C112.982 29.2195 112.746 28.4616 112.155 28.3699L110.65 28.1425C110.414 28.1066 110.211 27.9511 110.107 27.7317L109.432 26.3118C109.3 26.0325 109.041 25.8929 108.785 25.8929C108.526 25.8929 108.27 26.0325 108.138 26.3118L107.463 27.7317C107.36 27.9551 107.156 28.1066 106.92 28.1425L105.415 28.3699C104.824 28.4576 104.584 29.2195 105.016 29.6542L106.106 30.7591C106.277 30.9306 106.353 31.1819 106.313 31.4252L106.058 32.9887C105.978 33.4753 106.349 33.8702 106.769 33.8702C106.88 33.8702 106.996 33.8423 107.104 33.7825L108.454 33.0446C108.558 32.9887 108.673 32.9568 108.789 32.9568C108.905 32.9568 109.021 32.9847 109.125 33.0446L110.474 33.7825C110.586 33.8423 110.698 33.8702 110.81 33.8702C111.229 33.8702 111.6 33.4753 111.52 32.9887L111.265 31.4252C111.225 31.1819 111.301 30.9306 111.473 30.7591L112.555 29.6542ZM110.794 30.0411C110.394 30.444 110.215 31.0263 110.307 31.5967L110.482 32.6577L109.568 32.1591C109.328 32.0275 109.057 31.9557 108.781 31.9557C108.506 31.9557 108.238 32.0235 107.995 32.1591L107.08 32.6577L107.256 31.5967C107.352 31.0263 107.168 30.444 106.769 30.0411L106.03 29.2913L107.052 29.1357C107.603 29.0519 108.078 28.693 108.322 28.1744L108.777 27.2092L109.232 28.1744C109.48 28.693 109.955 29.0519 110.502 29.1357L111.524 29.2913L110.794 30.0411Z'
                            fill='#1E2481'
                        />
                        <rect x='99.9207' y='41.8472' width='58.5' height='16.8409' rx='1.77273' fill='#D3CCFF' />
                        <path
                            d='M112.56 50.0403C112.987 49.6056 112.752 48.8477 112.161 48.756L110.655 48.5286C110.42 48.4927 110.216 48.3372 110.112 48.1178L109.437 46.6979C109.305 46.4187 109.046 46.2791 108.79 46.2791C108.531 46.2791 108.275 46.4187 108.143 46.6979L107.469 48.1178C107.365 48.3412 107.161 48.4927 106.925 48.5286L105.42 48.756C104.829 48.8437 104.59 49.6056 105.021 50.0403L106.111 51.1452C106.283 51.3167 106.358 51.568 106.319 51.8113L106.063 53.3748C105.983 53.8614 106.354 54.2563 106.774 54.2563C106.886 54.2563 107.001 54.2284 107.109 54.1686L108.459 53.4307C108.563 53.3748 108.678 53.3429 108.794 53.3429C108.91 53.3429 109.026 53.3708 109.13 53.4307L110.479 54.1686C110.591 54.2284 110.703 54.2563 110.815 54.2563C111.234 54.2563 111.605 53.8614 111.526 53.3748L111.27 51.8113C111.23 51.568 111.306 51.3167 111.478 51.1452L112.56 50.0403ZM110.799 50.4272C110.4 50.8301 110.22 51.4124 110.312 51.9828L110.487 53.0438L109.573 52.5452C109.333 52.4136 109.062 52.3418 108.786 52.3418C108.511 52.3418 108.243 52.4096 108 52.5452L107.085 53.0438L107.261 51.9828C107.357 51.4124 107.173 50.8301 106.774 50.4272L106.035 49.6774L107.057 49.5218C107.608 49.4381 108.084 49.0791 108.327 48.5606L108.782 47.5953L109.238 48.5606C109.485 49.0791 109.96 49.4381 110.507 49.5218L111.53 49.6774L110.799 50.4272Z'
                            fill='#1E2481'
                        />
                        <rect x='99.9207' y='62.2339' width='58.5' height='16.8409' rx='1.77273' fill='#D3CCFF' />
                        <path
                            d='M112.555 70.427C112.982 69.9922 112.746 69.2344 112.155 69.1427L110.65 68.9153C110.414 68.8794 110.211 68.7238 110.107 68.5045L109.432 67.0845C109.3 66.8053 109.041 66.6657 108.785 66.6657C108.526 66.6657 108.27 66.8053 108.138 67.0845L107.463 68.5045C107.36 68.7278 107.156 68.8794 106.92 68.9153L105.415 69.1427C104.824 69.2304 104.584 69.9922 105.016 70.427L106.106 71.5318C106.277 71.7034 106.353 71.9546 106.313 72.1979L106.058 73.7615C105.978 74.2481 106.349 74.643 106.769 74.643C106.88 74.643 106.996 74.6151 107.104 74.5552L108.454 73.8173C108.558 73.7615 108.673 73.7296 108.789 73.7296C108.905 73.7296 109.021 73.7575 109.125 73.8173L110.474 74.5552C110.586 74.6151 110.698 74.643 110.81 74.643C111.229 74.643 111.6 74.2481 111.52 73.7615L111.265 72.1979C111.225 71.9546 111.301 71.7034 111.473 71.5318L112.555 70.427ZM110.794 70.8139C110.394 71.2167 110.215 71.7991 110.307 72.3695L110.482 73.4304L109.568 72.9319C109.328 72.8002 109.057 72.7284 108.781 72.7284C108.506 72.7284 108.238 72.7962 107.995 72.9319L107.08 73.4304L107.256 72.3695C107.352 71.7991 107.168 71.2167 106.769 70.8139L106.03 70.064L107.052 69.9085C107.603 69.8247 108.078 69.4657 108.322 68.9472L108.777 67.982L109.232 68.9472C109.48 69.4657 109.955 69.8247 110.502 69.9085L111.524 70.064L110.794 70.8139Z'
                            fill='#1E2481'
                        />
                        <defs>
                            <clipPath id={`${id}-clip`}>
                                <path
                                    d='M99.9207 17.9158V71.0976H79.5343C78.5552 71.0976 77.7616 70.304 77.7616 69.3249V17.9158H99.9207Z'
                                    fill='white'
                                />
                            </clipPath>
                        </defs>
                    </svg>
                    <Stack alignItems={'center'} spacing={2}>
                        <Text color={'primary'} variant={'p3'}>
                            {t('feedback-section.settings.ai-generated-feedback', 'AI Generated Feedback')}
                        </Text>
                        <Text align={'center'} variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.system-based-feedback', 'General & Detailed Feedback')}
                        </Text>
                        <Text align={'center'}>
                            {t(
                                'feedback-section.settings.system-based-feedback-description',
                                'Feedback will include a paragraph of general feedback as well as up to 5 targeted feedback suggestions.'
                            )}
                        </Text>
                    </Stack>
                </Stack>
            );
        }
        if (type === 'templated') {
            return (
                <Stack spacing={3}>
                    <svg
                        width='222'
                        height='81'
                        viewBox='0 0 222 81'
                        fill='none'
                        role='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <rect x='63.5801' width='94.84' height='24.3179' rx='1.77271' fill='#D3CCFF' />
                        <path
                            d='M72.9172 17.2989C70.1872 17.2989 68.0172 15.0869 68.0172 12.3569C68.0172 9.62693 70.1872 7.41493 72.9172 7.41493C74.4012 7.41493 75.6892 8.04494 76.5432 9.05293L75.6192 9.83694C74.9752 9.08094 73.9952 8.59093 72.9172 8.59093C70.8872 8.59093 69.2912 10.2849 69.2912 12.3569C69.2912 14.4149 70.8872 16.1229 72.9172 16.1229C73.9952 16.1229 74.9752 15.6329 75.6192 14.8769L76.5432 15.6609C75.6892 16.6689 74.4012 17.2989 72.9172 17.2989ZM80.5252 7.89093H80.5672V17.1589H79.3492V9.66894L77.4032 10.4669V9.29094L80.5252 7.89093Z'
                            fill='#1E2481'
                        />
                        <path
                            d='M93.1958 7.90499C95.2958 7.90499 96.9439 9.34693 97.332 11.14C97.4755 11.7998 97.4596 12.449 97.2895 13.0768C96.5718 15.7319 98.6717 16.8014 96.3166 16.2108C95.535 16.0139 94.7163 15.9607 93.9242 16.0991C93.3075 16.2055 92.6854 16.1736 92.0741 16.0086C90.4632 15.5776 89.2351 14.2155 89.0118 12.6139C88.645 10.0333 90.7024 7.90499 93.1958 7.90499ZM93.1958 6.84082C90.0272 6.84082 87.5072 9.57041 87.9485 12.7629C88.2303 14.7955 89.772 16.5034 91.7923 17.0408C92.5419 17.243 93.3181 17.2856 94.0996 17.1473C94.7482 17.0355 95.4181 17.0834 96.0561 17.243L96.8323 17.4346C97.8371 17.6847 98.7462 16.7908 98.491 15.8064C98.491 15.8064 98.3474 15.253 98.3421 15.2371C98.1826 14.6199 98.1454 13.9707 98.3155 13.3535C98.5229 12.5926 98.5548 11.7679 98.3687 10.9113C97.8796 8.66054 95.8062 6.84082 93.1958 6.84082Z'
                            fill='#1E2481'
                        />
                        <rect x='63.5801' y='27.8632' width='94.84' height='24.3179' rx='1.77271' fill='#D3CCFF' />
                        <path
                            d='M72.7551 45.1622C70.0251 45.1622 67.8551 42.9502 67.8551 40.2202C67.8551 37.4902 70.0251 35.2782 72.7551 35.2782C74.2391 35.2782 75.5271 35.9082 76.3811 36.9162L75.4571 37.7002C74.8131 36.9442 73.8331 36.4542 72.7551 36.4542C70.7251 36.4542 69.1291 38.1482 69.1291 40.2202C69.1291 42.2782 70.7251 43.9862 72.7551 43.9862C73.8331 43.9862 74.8131 43.4962 75.4571 42.7402L76.3811 43.5242C75.5271 44.5322 74.2391 45.1622 72.7551 45.1622ZM79.6071 43.8462H83.3171V45.0222H77.5631V44.4762L81.0071 40.3182C81.5811 39.6182 81.9731 38.8762 81.9731 38.4562C81.9731 37.5322 81.3151 36.9162 80.3211 36.9162C79.3271 36.9162 78.6411 37.4762 78.5711 38.3022L77.4791 38.0642C77.6611 36.6782 78.7531 35.7542 80.3211 35.7542C82.0431 35.7542 83.1911 36.8322 83.1911 38.4562C83.1911 39.1702 82.7291 40.1082 82.1271 40.8222L79.6071 43.8462Z'
                            fill='#1E2481'
                        />
                        <path
                            d='M95.1958 35.7682C97.2958 35.7682 98.9439 37.2102 99.332 39.0033C99.4755 39.6631 99.4596 40.3122 99.2895 40.9401C98.5718 43.5951 100.672 44.6646 98.3166 44.074C97.535 43.8771 96.7163 43.8239 95.9242 43.9623C95.3075 44.0687 94.6854 44.0368 94.0741 43.8718C92.4632 43.4408 91.2351 42.0787 91.0118 40.4771C90.645 37.8965 92.7024 35.7682 95.1958 35.7682ZM95.1958 34.704C92.0272 34.704 89.5072 37.4336 89.9485 40.6261C90.2303 42.6587 91.772 44.3667 93.7923 44.9041C94.5419 45.1063 95.3181 45.1488 96.0996 45.0105C96.7482 44.8987 97.4181 44.9466 98.0561 45.1063L98.8323 45.2978C99.8371 45.5479 100.746 44.654 100.491 43.6696C100.491 43.6696 100.347 43.1163 100.342 43.1003C100.183 42.4831 100.145 41.834 100.316 41.2167C100.523 40.4559 100.555 39.6311 100.369 38.7745C99.8796 36.5238 97.8062 34.704 95.1958 34.704Z'
                            fill='#1E2481'
                        />
                        <rect x='63.5801' y='55.7267' width='94.84' height='24.3179' rx='1.77271' fill='#D3CCFF' />
                        <path
                            d='M72.7141 73.0257C69.9841 73.0257 67.8141 70.8137 67.8141 68.0837C67.8141 65.3537 69.9841 63.1417 72.7141 63.1417C74.1981 63.1417 75.4861 63.7717 76.3401 64.7797L75.4161 65.5637C74.7721 64.8077 73.7921 64.3177 72.7141 64.3177C70.6841 64.3177 69.0881 66.0117 69.0881 68.0837C69.0881 70.1417 70.6841 71.8497 72.7141 71.8497C73.7921 71.8497 74.7721 71.3597 75.4161 70.6037L76.3401 71.3877C75.4861 72.3957 74.1981 73.0257 72.7141 73.0257ZM81.7781 67.9717C82.7301 68.2797 83.3741 69.1337 83.3741 70.2117C83.3741 71.8917 82.0581 73.0117 80.1961 73.0117C78.9081 73.0117 77.7881 72.2977 77.2421 71.1077L78.2501 70.5337C78.6561 71.3597 79.3701 71.8637 80.1541 71.8637C81.3021 71.8637 82.1421 71.2197 82.1421 70.2537C82.1421 69.2177 81.3301 68.5317 80.1961 68.5317H79.2441V67.4817H80.1121C81.0781 67.4817 81.7081 66.9077 81.7081 66.0817C81.7081 65.2977 81.1201 64.7797 80.2381 64.7797C79.6081 64.7797 78.9641 65.1157 78.6141 65.6477L77.7741 64.9477C78.2361 64.1497 79.2301 63.6177 80.2661 63.6177C81.8621 63.6177 82.9261 64.5837 82.9261 66.0537C82.9261 66.9217 82.4921 67.6357 81.7781 67.9717Z'
                            fill='#1E2481'
                        />
                        <path
                            d='M95.1958 63.6317C97.2958 63.6317 98.9439 65.0737 99.332 66.8668C99.4755 67.5266 99.4596 68.1757 99.2895 68.8036C98.5718 71.4587 100.672 72.5282 98.3166 71.9375C97.535 71.7407 96.7163 71.6875 95.9242 71.8258C95.3075 71.9322 94.6854 71.9003 94.0741 71.7354C92.4632 71.3044 91.2351 69.9422 91.0118 68.3407C90.645 65.7601 92.7024 63.6317 95.1958 63.6317ZM95.1958 62.5676C92.0272 62.5676 89.5072 65.2972 89.9485 68.4896C90.2303 70.5222 91.772 72.2302 93.7923 72.7676C94.5419 72.9698 95.3181 73.0124 96.0996 72.874C96.7482 72.7623 97.4181 72.8102 98.0561 72.9698L98.8323 73.1613C99.8371 73.4114 100.746 72.5175 100.491 71.5332C100.491 71.5332 100.347 70.9798 100.342 70.9638C100.183 70.3466 100.145 69.6975 100.316 69.0803C100.523 68.3194 100.555 67.4947 100.369 66.638C99.8796 64.3873 97.8062 62.5676 95.1958 62.5676Z'
                            fill='#1E2481'
                        />
                    </svg>
                    <Stack alignItems={'center'} spacing={2}>
                        <Text color={'primary'} variant={'p3'}>
                            {t('feedback-section.settings.manual-feedback', 'Manual Feedback')}
                        </Text>
                        <Text align={'center'} variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.templated-feedback', 'Smart Feedback Templates')}
                        </Text>
                        <Text align={'center'}>
                            {t(
                                'feedback-section.settings.templated-feedback-description',
                                'Predefine specific feedback for each rating within a rubric criterion. When grading, this feedback is automatically applied.'
                            )}
                        </Text>
                    </Stack>
                </Stack>
            );
        }
        if (type === 'custom') {
            return (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <svg
                        width='222'
                        height='81'
                        viewBox='0 0 222 81'
                        fill='none'
                        role='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <rect x='89' y='18.075' width='44' height='44' rx='4' fill='#D3CCFF' />
                        <path
                            d='M119.731 33.6732L117.341 31.3274C116.991 30.9819 116.425 30.991 116.075 31.3547L102.26 45.5745C102.09 45.7382 102 45.9655 102 46.211V48.3385C102 48.8386 102.404 49.075 102.898 49.075H104.955C105.189 49.075 105.413 49.0659 105.584 48.9022L119.731 34.9551C120.09 34.6005 120.09 34.0277 119.731 33.6732ZM117.09 35.2097L105.961 46.4746C105.611 46.8292 105.045 46.8292 104.695 46.4746C104.344 46.1201 104.344 45.5473 104.695 45.1927L115.823 33.9277C116.174 33.5731 116.74 33.5731 117.09 33.9277C117.44 34.2823 117.44 34.8551 117.09 35.2097Z'
                            fill='#1E2481'
                        />
                    </svg>

                    <Stack alignItems={'center'} spacing={2}>
                        <Text color={'primary'} variant={'p3'}>
                            {t('feedback-section.settings.ai-generated-feedback', 'AI Generated Feedback')}
                        </Text>
                        <Text align={'center'} variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.custom-feedback', 'Custom Feedback Structure')}
                        </Text>
                        <Text align={'center'}>
                            {t(
                                'feedback-section.settings.custom-feedback-description',
                                'You will be responsible for defining the structure, tone and content of feedback.'
                            )}
                        </Text>
                    </Stack>
                </Stack>
            );
        }
        return null;
    };

    return (
        <CustomBox $isQuizEdit={isQuizEdit} {...rest}>
            <CustomCard
                component={'button'}
                type={'button'}
                role={'button'}
                aria-pressed={isSelected}
                aria-disabled={disabled}
                $feedbackType={type}
                $isQuizEdit={isQuizEdit}
                $isSelected={isSelected}
                onClick={disabled ? undefined : onClick}
            >
                {getCardContent(type)}
            </CustomCard>
        </CustomBox>
    );
};

const CustomBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$isQuizEdit',
})(({ $isQuizEdit }) => {
    return {
        width: '100%',
        minHeight: $isQuizEdit ? '312px' : '256px',
        '& svg': {
            maxWidth: $isQuizEdit ? 'none' : '100%',
            ...($isQuizEdit && { width: '222px', height: '81px' }),
            ...(!$isQuizEdit && { height: 'auto' }),
        },
    };
});

const CustomCard = styled(Card, {
    shouldForwardProp: (prop) => !['$feedbackType', '$isQuizEdit', '$isSelected'].includes(prop),
})(({ theme, $feedbackType, $isQuizEdit, $isSelected }) => {
    const boxShadow = $isQuizEdit
        ? theme.palette.ensightful.shadows[$feedbackType === 'system' ? 2 : 1]
        : theme.palette.ensightful.shadows[2];

    return {
        width: '100%',
        alignItems: 'flex-start',
        height: '100%',
        padding: theme.spacing(5),
        boxShadow,
        borderRadius: '16px',
        transition: 'all 0.2s ease-in-out',
        '& > *': {
            width: '100%',
            alignItems: 'center',
        },
        '&[aria-disabled="true"]': {
            opacity: 0.6,
            cursor: 'default',
        },

        '&:hover, &:active': {
            margin: 0,
            width: '100%',
            height: '100%',
            border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        },

        ...(!$isSelected && {
            '&:hover': {
                backgroundColor: theme.palette.ensightful.neutral[50],
            },
            '&:active': {
                backgroundColor: theme.palette.ensightful.neutral[100],
            },
        }),

        ...($isSelected && {
            border: `1px solid ${theme.palette.ensightful.primary[500]}`,
            boxShadow: $isQuizEdit ? boxShadow : `${boxShadow}, 0 0 0 1px ${theme.palette.ensightful.primary[500]}`,

            '&:hover': {
                border: `1px solid ${theme.palette.ensightful.primary[500]}`,
            },
        }),
    };
});

export default FeedbackTypeCard;
